import {Fragment, HTMLProps, useState} from 'react';
import {useTranslation} from 'react-i18next';
import ICON_FOOTER_TOGGLE from '../../../../public/static/icon-home-footer-toggle.svg';
import Anchor from '../../../common/component/Anchor';
import Text from '../../../common/component/Text';
import {REGION} from '../../../common/constant';
import {Page} from '../../../common/constant/page';
import useScreenType from '../../../common/hook/useScreenType';
import {IRegion} from '../../../common/type';
import {PageInfo} from '../../../common/util/PageRouter';

interface IProps extends HTMLProps<HTMLDivElement> {
  isBottomFixed?: boolean;
  isOnlyMobile?: boolean;
  alwaysShowFooter?: boolean;
}

export default function HomeFooter({
  isBottomFixed = false,
  className,
  isOnlyMobile = false,
  alwaysShowFooter = false,
  ...rest
}: IProps) {
  const {isLarge, isSmall} = useScreenType();
  const [showInfo, setShowInfo] = useState(false);
  const isKorea = REGION === 'kor';
  const {t} = useTranslation();

  // TODO: 모든 region 적용될 예정.
  if (!alwaysShowFooter && !(REGION === 'kor' || REGION === 'idn')) {
    return null;
  }

  const TEXT_KEY_MAP =
    !isOnlyMobile && isLarge ? DESKTOP_TEXT_KEY_MAP : MOBILE_TEXT_KEY_MAP;

  return (
    <div
      className={cn('w-full pt-50 pb-30 flex-center flex-col', className, {
        'fixed left-0 bottom-0': isBottomFixed,
      })}
      {...rest}
    >
      <div className="flex-center">
        {TEXT_KEY_MAP.map(
          ({key, pageInfo, isBold, isToggle, isNewTab}, index) => (
            <Fragment key={index}>
              {index > 0 && (
                <div
                  className={cn(
                    'w-1 bg-grey-08 h-11',
                    isKorea && isSmall ? 'mx-6' : 'mx-8'
                  )}
                />
              )}
              <Anchor
                pageInfo={pageInfo}
                display={isToggle ? 'flex' : 'block'}
                className={cn({
                  'flex-center': isToggle,
                })}
                onClick={isToggle ? () => setShowInfo(!showInfo) : undefined}
                target={isNewTab ? '_blank' : undefined}
              >
                <Text
                  className={cn(
                    isBold
                      ? isKorea
                        ? 's13-bold-grey10'
                        : 's13-bold-grey08'
                      : 's13-medium-grey08',
                    {
                      '!text-[12px]': isKorea && isSmall,
                    }
                  )}
                >
                  {t(key)}
                </Text>
                {isToggle && (
                  <img
                    src={ICON_FOOTER_TOGGLE}
                    width={8}
                    alt=""
                    className={cn('ml-3', {
                      'scale-y-[-1]': showInfo,
                    })}
                  />
                )}
              </Anchor>
            </Fragment>
          )
        )}
      </div>
      {isLarge && showInfo && (
        <div className="mt-9">
          {DESKTOP_INFO_TEXT[isKorea ? 'kor' : 'idn'].map((textList, index) => (
            <div
              key={index}
              className={cn('flex-center', {
                'mt-3': index > 0,
              })}
            >
              {Object.keys(textList).map((key, index2) => (
                <Fragment key={index2}>
                  {index2 > 0 && <div className="w-1 mx-6 bg-grey-04 h-9" />}
                  <Text className="s11-medium-grey05 !whitespace-nowrap">{`${t(
                    'more_serviceinfo_business_' + key
                  )} : ${t(
                    'more_serviceinfo_business_' + textList[key]
                  )}`}</Text>
                </Fragment>
              ))}
            </div>
          ))}
        </div>
      )}
      <Text className="mt-8 s12-medium-grey05 flex-center">
        {t(isOnlyMobile ? 'footer_copyright_korea' : 'footer_copyright')}
      </Text>
    </div>
  );
}

const DESKTOP_INFO_TEXT: {
  [key in Extract<IRegion, 'kor' | 'idn'>]: Array<{[key: string]: string}>;
} = {
  kor: [
    {
      name_company: 'name_corporation',
      name_representative: 'name_representative_detail',
      phone: 'phone_detail',
      registration_number: 'registration_number_detail',
    },
    {
      address: 'address_detail',
      mail_order: 'mail_order_detail',
    },
    {
      email: 'email_detail',
      host: 'name_corporation',
    },
  ],
  idn: [
    {
      name_company: 'name_corporation',
      name_representative: 'name_representative_detail',
    },
    {
      phone: 'phone_detail',
      registration_number: 'registration_number_detail',
    },
    {
      address: 'address_detail',
      mail_order: 'mail_order_detail',
    },
    {
      email: 'email_detail',
      host: 'name_corporation',
    },
  ],
};

interface IFooterTextItem {
  key: string;
  pageInfo?: PageInfo | string;
  isBold?: boolean;
  isToggle?: boolean;
  isNewTab?: boolean;
}

const PRIVACY: IFooterTextItem = {
  key: 'footer_privacy',
  pageInfo: {page: Page.MorePolicyPersonalInformation},
  isBold: true,
};

const CUSTOMER_SERVICE: IFooterTextItem = {
  key: 'footer_customer_service',
  pageInfo: {page: Page.MoreCustomerCenter},
};

const BUSINESS_INFO: IFooterTextItem = {
  key: 'footer_business_info',
  isToggle: true,
};

const BUSINESS_INFO_MOBILE: IFooterTextItem = {
  key: 'footer_business_info',
  pageInfo: {page: Page.MoreBusiness},
};

const TERMS_OF_USE: IFooterTextItem = {
  key: 'footer_terms_of_use',
  pageInfo: {page: Page.MorePolicy},
};

const TERMS_OF_USE_MOBILE: IFooterTextItem = {
  key: 'footer_terms_of_use',
  pageInfo: {page: Page.MorePolicyTerms},
};

const BUSINESS_INFO_CONFIRM: IFooterTextItem = {
  key: 'footer_business_info_confirm',
  pageInfo: 'https://www.ftc.go.kr/bizCommPop.do?wrkr_no=2208802594',
  isNewTab: true,
};

const YOUTH_PROTECTION: IFooterTextItem = {
  key: 'footer_youth_protection',
  pageInfo: {page: Page.MorePolicyYouth},
};

const COOKIE_POLICY: IFooterTextItem = {
  key: 'footer_cookie_policy',
  pageInfo: {page: Page.MorePolicyCookieTerms},
};

const MOBILE_TEXT_KEY_MAP =
  REGION === 'kor'
    ? [PRIVACY, TERMS_OF_USE_MOBILE, CUSTOMER_SERVICE, BUSINESS_INFO_MOBILE]
    : [CUSTOMER_SERVICE, BUSINESS_INFO_MOBILE];

const DESKTOP_TEXT_KEY_MAP =
  REGION === 'kor'
    ? [
        BUSINESS_INFO,
        TERMS_OF_USE,
        PRIVACY,
        YOUTH_PROTECTION,
        CUSTOMER_SERVICE,
        BUSINESS_INFO_CONFIRM,
      ]
    : REGION === 'twn'
      ? [BUSINESS_INFO, TERMS_OF_USE, PRIVACY, CUSTOMER_SERVICE]
      : [BUSINESS_INFO, TERMS_OF_USE, PRIVACY, COOKIE_POLICY, CUSTOMER_SERVICE];
